import { Close } from '@mui/icons-material';
import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { BracketMatch, Player } from '../components/room/types';
import './styles.css';

type PropTypes = {
  bracketMatches?: Record<string, BracketMatch>;
  handleUpdateBracketDataSubmit: (
    matchKey: string,
    match?: BracketMatch
  ) => void;
  openUpdateBracketDataDialog: boolean;
  setOpenUpdateBracketDataDialog: (open: boolean) => void;
  sortedPlayers?: Player[];
};

export const UpdateBracketDataDialog = ({
  bracketMatches,
  handleUpdateBracketDataSubmit,
  openUpdateBracketDataDialog,
  setOpenUpdateBracketDataDialog,
  sortedPlayers,
}: PropTypes) => {
  const [matchKey, setMatchKey] = useState('00');
  const [selectedMatch, setSelectedMatch] = useState<BracketMatch>();
  const [top8Players, setTop8Players] = useState<Player[]>();
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>();

  useEffect(() => {
    if (!bracketMatches) return;
    setSelectedMatch(bracketMatches['00']);
  }, [bracketMatches]);

  useEffect(() => {
    if (!sortedPlayers) return;
    console.log('sortedPlayers', sortedPlayers.slice(0, 8));
    setTop8Players(sortedPlayers.slice(0, 8));
  }, [sortedPlayers]);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const handleToggleChange = (event: any, matchKey: string) => {
    if (matchKey !== null && bracketMatches) {
      setMatchKey(matchKey);
      setSelectedMatch(bracketMatches[matchKey]);
    }
  };

  const handlePlayerSelectChange = (event: any, profileIds: string[]) => {
    if (profileIds.length > 2) return;
    setSelectedPlayers(profileIds);
    if (!selectedMatch) return;
    const newState: BracketMatch = {
      ...selectedMatch,
      profileIds: [...profileIds],
      usernames: profileIds.map(
        (id) => top8Players?.find((t) => t.profileId === id)?.username || ''
      ),
    };
    setSelectedMatch(newState);
  };

  const handleUpdateResult = (
    index: number,
    action: 'decrement' | 'increment'
  ) => {
    if (!selectedMatch) return;
    const newResult: [number, number] = [...selectedMatch.results];
    newResult[index] =
      action === 'decrement' ? newResult[index] - 1 : newResult[index] + 1;
    const newState: BracketMatch = {
      ...selectedMatch,
      results: newResult,
    };
    setSelectedMatch(newState);
  };

  const dialogTitleText = 'Update Bracket';
  const closeButton = (
    <IconButton onClick={() => setOpenUpdateBracketDataDialog(false)}>
      <Close />
    </IconButton>
  );

  return (
    <Dialog
      fullScreen={fullScreen}
      open={openUpdateBracketDataDialog}
      onClose={() => setOpenUpdateBracketDataDialog(false)}
      aria-labelledby="responsive-dialog-title"
    >
      <DialogTitle
        id="responsive-dialog-title"
        className="diaolog-title-with-close-button"
      >
        {dialogTitleText}
        {closeButton}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={4}>
          <Typography variant="h6" color="secondary">
            Can only update 1 match at a time
          </Typography>
          <Typography variant="h6">Match Key</Typography>
          <Stack direction="row" spacing={2}>
            <ToggleButtonGroup
              color="primary"
              value={matchKey}
              exclusive
              onChange={handleToggleChange}
              aria-label="Platform"
            >
              <ToggleButton value="00">00</ToggleButton>
              <ToggleButton value="01">01</ToggleButton>
              <ToggleButton value="02">02</ToggleButton>
              <ToggleButton value="03">03</ToggleButton>
              <ToggleButton value="10">10</ToggleButton>
              <ToggleButton value="11">11</ToggleButton>
              <ToggleButton value="20">20</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Typography variant="h6">Players</Typography>
          {top8Players ? (
            <Stack direction="row" spacing={2}>
              <ToggleButtonGroup
                color="primary"
                value={selectedPlayers}
                onChange={handlePlayerSelectChange}
                aria-label="Platform"
              >
                {top8Players.map((p) => (
                  <ToggleButton value={p.profileId}>{p.username}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
          ) : null}
          <Typography variant="h6">Series</Typography>
          <Stack direction={'row'} spacing={2}>
            <Typography>{selectedMatch?.usernames[0]}</Typography>
            <ButtonGroup
              variant="contained"
              aria-label="outlined primary button group"
            >
              <Button
                onClick={() => handleUpdateResult(0, 'decrement')}
                disabled={selectedMatch?.results[0] === 0}
              >
                -
              </Button>
              <Button
                onClick={() => handleUpdateResult(0, 'increment')}
                disabled={selectedMatch?.results[0] === 2}
              >
                +
              </Button>
            </ButtonGroup>
            <Typography>{selectedMatch?.results[0]}</Typography>
          </Stack>
          <Stack direction={'row'} spacing={2}>
            <Typography>{selectedMatch?.usernames[1]}</Typography>
            <ButtonGroup
              variant="contained"
              aria-label="outlined primary button group"
            >
              <Button
                onClick={() => handleUpdateResult(1, 'decrement')}
                disabled={selectedMatch?.results[1] === 0}
              >
                -
              </Button>
              <Button
                onClick={() => handleUpdateResult(1, 'increment')}
                disabled={selectedMatch?.results[1] === 2}
              >
                +
              </Button>
            </ButtonGroup>
            <Typography>{selectedMatch?.results[1]}</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ margin: 3 }}>
        <Button
          variant="text"
          onClick={() => setOpenUpdateBracketDataDialog(false)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleUpdateBracketDataSubmit(matchKey, selectedMatch)}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};
